/**
 * `/products/print/catalogue` — the event catalogue as a printable sheet.
 *
 * Reached from the "Share this catalogue" panel on `/products` in tag mode, so
 * a customer who wants paper (or a PDF over email) gets the same catalogue the
 * share links publish, at the same three pricing modes.
 *
 * Two things it deliberately does NOT reuse from the lead documents:
 *
 *  - `PrintDocumentPage`, whose Back link takes a required `leadId` and whose
 *    masthead has a "Prepared for" block. This catalogue belongs to an event
 *    and is addressed to nobody. It composes the same PARTS — `PrintHeader`,
 *    `PrintFooter`, `PrintButton`, `print.css` — without bending a frame built
 *    for a different document.
 *  - `PrintCatalogueTable`, which is a table of thumbnails. A trade catalogue
 *    sells on the photograph, so this is the `/products` grid on paper.
 *
 * It shows the products the SCREEN was filtered to, intersected with the
 * event's tagged set — see `taggedAndFiltered` below for why the intersection
 * is not optional.
 */
import { useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDimensions, formatMoney, NO_AMOUNT_LABEL, PriceMode, publicPrice } from '@paradise/shared';
import { z } from 'zod';

import { PrintButton } from '@/components/print/print-button';
import { PrintFooter, PrintHeader } from '@/components/print/print-header';
import '@/components/print/print.css';
import { Button } from '@/components/ui/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { useCatalogueProducts, useCategories, useEventTaggedProductIds } from '@/lib/catalogue-data';
import {
  ALL_KEY,
  ANY_FINISH_KEY,
  ANY_PRICE_KEY,
  applyFinishFilter,
  applyPriceBandFilter,
  scopeProducts
} from '@/lib/catalogue-filter';
import { useEventsQuery } from '@/lib/events/use-events-query';
import { mediaUrl } from '@/lib/media';

/**
 * The filters arrive in the URL so the sheet is reproducible and shareable —
 * the same link prints the same pages tomorrow. `cols` rides along too, so a
 * staff member who found the density they like can bookmark it.
 */
const PrintSearch = z.object({
  event: z.string(),
  price: PriceMode.catch('NONE'),
  cat: z.string().optional().catch(undefined),
  fin: z.string().optional().catch(undefined),
  // The screen's price-band filter. Distinct from `price` above, which is the
  // PriceMode this sheet PRINTS at — one narrows the rows, the other decides
  // what each surviving row says about money.
  band: z.string().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  cols: z.coerce.number().int().min(2).max(4).catch(3)
});

export const Route = createFileRoute('/_app/products/print/catalogue')({
  // Full-bleed, outside the app bar and bottom tab bar — the same contract
  // every other print route uses (see components/shell/app-shell.tsx). Not
  // merely a `print:hidden` on the chrome: the tab bar is `fixed`, so on paper
  // it lands on top of the grid rather than politely at the end.
  staticData: { hideShellChrome: true },
  validateSearch: PrintSearch,
  component: PrintCataloguePage
});

const COLUMN_CHOICES = [2, 3, 4] as const;

/** What one card's price line says, given the mode this sheet is being printed at. */
function priceLabel(mode: PriceMode, listPrice: string | null | undefined, currency: string): string | null {
  const shown = publicPrice(mode, listPrice ?? null);
  if ('unitPrice' in shown) return formatMoney(shown.unitPrice, currency);
  if ('priceBand' in shown) return shown.priceBand;
  // NONE: no price line at all, rather than a row of em-dashes down the page.
  return null;
}

function PrintCataloguePage() {
  // `cols` is read straight off the URL rather than mirrored into state: two
  // copies would disagree the moment someone used the back button.
  const { event: eventId, price, cat, fin, band, q, cols } = Route.useSearch();
  const navigate = Route.useNavigate();

  const products = useCatalogueProducts();
  const { data: categories = [] } = useCategories();
  const { data: events = [] } = useEventsQuery();
  const taggedIds = useEventTaggedProductIds(eventId);

  const event = events.find(candidate => candidate.id === eventId) ?? null;
  const categoryNameById = useMemo(() => new Map(categories.map(c => [c.id, c.name])), [categories]);

  /**
   * The event's tagged set is intersected in ALWAYS, not only when the screen
   * was narrowing by it. The Print button lives in TAG mode, where the grid
   * deliberately shows the whole catalogue so untagged pieces can be ticked —
   * printing "what the screen showed" literally would put products that are
   * not in this event onto its catalogue. Today every active product happens
   * to be tagged to the one event, so that bug would be invisible until the
   * second show.
   *
   * The archived chip is likewise ignored: a catalogue prints what we sell.
   */
  const taggedAndFiltered = useMemo(() => {
    const active = products.filter(product => product.isActive);
    const scoped = scopeProducts(active, {
      activeChip: cat ?? ALL_KEY,
      query: (q ?? '').trim().toLowerCase(),
      restrictToIds: taggedIds,
      liveCategoryIds: categoryNameById
    });
    return applyPriceBandFilter(applyFinishFilter(scoped, fin ?? ANY_FINISH_KEY), band ?? ANY_PRICE_KEY);
  }, [products, cat, q, fin, band, taggedIds, categoryNameById]);

  /** Null while the tagged mirror is still loading — an empty sheet then reads as "loading", not "nothing tagged". */
  const loading = taggedIds === null;

  const subtitleParts = [event?.name, event?.city].filter((part): part is string => Boolean(part));
  const subtitle = subtitleParts.length > 0 ? subtitleParts.join(' · ') : 'Event catalogue';

  return (
    <div className="print-color-exact mx-auto max-w-204 bg-background p-6 text-foreground print:p-0">
      <PrintHeader title="Catalogue" subtitle={subtitle} />

      <div className="mt-3.5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] tracking-wider text-muted-foreground uppercase">
            {price === 'NONE' ? 'Without prices' : price === 'BAND' ? 'Price bands' : 'Prices'}
          </div>
          <div className="text-[12.5px] text-muted-foreground">
            {loading ? 'Loading…' : `${String(taggedAndFiltered.length)} pieces`}
          </div>
        </div>

        <div className="flex items-center gap-2 print:hidden">
          {/* Density is a print-time judgement — how much paper against how big
              the photographs need to be — so it is chosen here, at the page,
              rather than guessed for staff back on the share panel. */}
          <div className="flex items-center gap-1 rounded-lg border border-border p-0.5">
            {COLUMN_CHOICES.map(choice => (
              <Button
                key={choice}
                type="button"
                size="sm"
                variant={cols === choice ? 'default' : 'ghost'}
                onClick={() => void navigate({ search: prev => ({ ...prev, cols: choice }), replace: true })}
              >
                {choice} across
              </Button>
            ))}
          </div>
          <Button variant="ghost" size="sm" render={<Link to="/products" search={{ cat, fin, q }} />}>
            Back
          </Button>
          <PrintButton />
        </div>
      </div>

      {!loading && taggedAndFiltered.length === 0 ? (
        <Empty className="mt-6 border border-border print:hidden">
          <EmptyHeader>
            <EmptyTitle>Nothing to print</EmptyTitle>
            <EmptyDescription>
              No product tagged to this event matches the filters you had on. Clear them and try again.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div
          className="mt-4 grid gap-x-4 gap-y-5"
          style={{ gridTemplateColumns: `repeat(${String(cols)}, minmax(0, 1fr))` }}
        >
          {taggedAndFiltered.map(product => {
            const cover = product.photos[0]?.coverPath;
            const measure = formatDimensions(product, product.dimensionUnit);
            const category = product.categoryId ? categoryNameById.get(product.categoryId) : undefined;
            const price_ = priceLabel(price, product.listPrice, product.currency);

            return (
              // break-inside-avoid keeps a card from being sliced by a page
              // break — half a photograph at the foot of a page is the one
              // thing that makes a printed catalogue look unfinished.
              <div key={product.id} className="flex break-inside-avoid flex-col gap-1">
                {cover === undefined ? (
                  <div className="flex aspect-square w-full items-center justify-center rounded border border-border text-[10px] text-muted-foreground">
                    No photo
                  </div>
                ) : (
                  // print-photo-exact — Chrome washes images out on paper
                  // otherwise, and a buyer judges brass and finish from this.
                  // Never tinted, never filtered.
                  <img
                    src={mediaUrl('products', cover)}
                    alt={product.name}
                    className="print-photo-exact aspect-square w-full rounded object-cover"
                  />
                )}
                <div className="text-[9px] tracking-wide text-muted-foreground uppercase">
                  #{product.itemNo}
                  {category ? ` · ${category}` : ''}
                </div>
                <div className="font-heading text-[12px] leading-tight font-semibold">{product.name}</div>
                {measure && <div className="text-[10px] text-muted-foreground">{measure}</div>}
                <div className="mt-auto flex items-baseline justify-between gap-2 pt-0.5">
                  <span className="text-[10px] text-muted-foreground">
                    MOQ {product.moq === null || product.moq === undefined ? NO_AMOUNT_LABEL : product.moq}
                  </span>
                  {price_ !== null && <span className="text-[11px] font-semibold">{price_}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <PrintFooter right={price === 'NONE' ? 'The Paradise' : `Prices in ${taggedAndFiltered[0]?.currency ?? 'INR'}`} />
    </div>
  );
}
